import React, { useEffect, useState } from 'react';
import { fetchDeliveryDetailsByRouteRef } from '../../../api/delivery_details';
import { getCompletionByUuid } from '../../../api/route_completions';

const NetworkFreightConsignmentReport = ({ routes = [], theme }) => {
    const isLightTheme = theme === 'light';
    const [routeDetails, setRouteDetails] = useState({});
    const columns = [
        ['customer_consignment_number', 'Customer Ref'],
        ['route_ref', 'Internal Ref'],
        ['date', 'Date'],
        ['customer_company', 'Customer'],
        ['delivery_name', 'Delivery'],
        ['delivery_postcode', 'Postcode'],
        ['route', 'Route'],
        ['pallet_count', 'Pallets'],
        ['weight', 'Weight'],
        ['adr_value', 'ADR'],
        ['driver', 'Driver'],
        ['truck_reg', 'Vehicle'],
        ['status', 'Status'],
    ];

    const displayValue = (route, key) => {
        if (key === 'date' && route.date) return new Date(`${route.date}T00:00:00`).toLocaleDateString('en-GB');
        if (key === 'weight' && route.weight != null) return `${Number(route.weight).toLocaleString('en-GB', { maximumFractionDigits: 2 })} kg`;
        if (key === 'status') {
            if (route.completed) return 'Completed';
            if (route.confirmed) return 'Confirmed';
            if (route.profile_id) return 'Allocated';
            return 'Unallocated';
        }
        return route[key] ?? '----';
    };

    useEffect(() => {
        let active = true;
        const loadingState = Object.fromEntries(
            routes.map((route) => [route.route_uuid || route.id, { loading: true }])
        );
        setRouteDetails(loadingState);

        Promise.all(routes.map(async (route) => {
            const key = route.route_uuid || route.id;
            const [completionResult, deliveryResult] = await Promise.all([
                route.route_uuid ? getCompletionByUuid(route.route_uuid) : Promise.resolve(null),
                route.route_ref ? fetchDeliveryDetailsByRouteRef(route.route_ref) : Promise.resolve(null),
            ]);
            return [key, {
                loading: false,
                completion: completionResult?.success ? completionResult.completion : null,
                delivery: deliveryResult?.status === 200 ? deliveryResult.data : null,
            }];
        })).then((entries) => {
            if (active) setRouteDetails(Object.fromEntries(entries));
        });

        return () => { active = false; };
    }, [routes]);

    const formatTime = (value) => value ? String(value).slice(0, 5) : '----';
    const formatDateTime = (value) => value ? new Date(value).toLocaleString('en-GB') : '----';

    const signatureUrl = (completion) => {
        const raw = completion?.customer_signature_data;
        if (!raw) return null;
        if (typeof raw === 'object') return raw.signature || null;
        if (!raw.startsWith('{')) return raw;
        try {
            return JSON.parse(raw)?.signature || null;
        } catch (_) {
            return null;
        }
    };

    const detailItem = (label, value) => (
        <div style={{ minWidth: 130 }}>
            <div style={{ fontSize: 12, opacity: 0.65, textTransform: 'uppercase' }}>{label}</div>
            <strong>{value ?? '----'}</strong>
        </div>
    );

    const imageList = (label, images, whiteBackground = false) => images?.length > 0 && (
        <div style={{ textAlign: 'center', minWidth: 260 }}>
            <div style={{ fontSize: 12, opacity: 0.65, textTransform: 'uppercase', marginBottom: 10 }}>
                {label} ({images.length})
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center', gap: 12 }}>
                {images.map((url, index) => (
                    <a key={`${url}-${index}`} href={url} target="_blank" rel="noreferrer" title={`Open ${label.toLowerCase()} ${index + 1}`}>
                        <img
                            src={url}
                            alt={`${label} ${index + 1}`}
                            style={{
                                width: whiteBackground ? 320 : 180,
                                height: whiteBackground ? 140 : 180,
                                objectFit: 'contain',
                                objectPosition: 'center',
                                background: whiteBackground ? '#fff' : isLightTheme ? '#eef0f2' : '#181818',
                                borderRadius: 6,
                                border: `1px solid ${isLightTheme ? '#b8bec6' : '#555'}`,
                            }}
                        />
                    </a>
                ))}
            </div>
        </div>
    );

    if (routes.length === 0) {
        return <p style={{ marginTop: 30 }}>No Network Freight job found for that consignment number.</p>;
    }

    return (
        <div style={{ margin: '20px', overflowX: 'auto' }}>
            <h3>Network Freight Consignment</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1200 }}>
                <thead>
                    <tr style={{ background: isLightTheme ? '#e5e7eb' : '#444' }}>
                        {columns.map(([key, label]) => (
                            <th key={key} style={{ padding: 10, border: '1px solid #aaa', textAlign: 'left' }}>{label}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {routes.map((route, index) => {
                        const key = route.route_uuid || route.id;
                        const details = routeDetails[key] || { loading: true };
                        const completion = details.completion;
                        const delivery = details.delivery;
                        const signature = signatureUrl(completion);
                        const latitude = Number(completion?.latitude);
                        const longitude = Number(completion?.longitude);
                        const hasCoordinates = completion?.latitude != null
                            && completion?.longitude != null
                            && Number.isFinite(latitude)
                            && Number.isFinite(longitude);
                        const rowBackground = isLightTheme
                            ? index % 2 === 0 ? '#fff' : '#f3f4f6'
                            : index % 2 === 0 ? '#2e2e2e' : '#383838';

                        return (
                            <React.Fragment key={key}>
                                <tr style={{ background: rowBackground, color: isLightTheme ? '#222' : '#eee' }}>
                                    {columns.map(([columnKey]) => (
                                        <td key={columnKey} style={{ padding: 10, border: '1px solid #aaa' }}>{displayValue(route, columnKey)}</td>
                                    ))}
                                </tr>
                                <tr style={{ background: isLightTheme ? '#f8fafc' : '#242424', color: isLightTheme ? '#222' : '#eee' }}>
                                    <td colSpan={columns.length} style={{ padding: 16, border: '1px solid #aaa' }}>
                                        {details.loading ? (
                                            <strong>Loading POD and delivery information...</strong>
                                        ) : (
                                            <div>
                                                <section style={{ paddingBottom: 18 }}>
                                                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 14 }}>
                                                        <h4 style={{ margin: 0 }}>Delivery Information</h4>
                                                        <span style={{ opacity: 0.6, fontSize: 13 }}>{route.delivery_name || route.customer_company}</span>
                                                    </div>
                                                    {delivery ? (
                                                        <div style={{ display: 'grid', gap: 18 }}>
                                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 18 }}>
                                                                {detailItem('Booked', formatTime(delivery.booked_time))}
                                                                {detailItem('Arrival', formatTime(delivery.arrival_time))}
                                                                {detailItem('Departure', formatTime(delivery.departure_time))}
                                                                {detailItem('Time On Site', delivery.delay_length == null ? '----' : `${delivery.delay_length} mins`)}
                                                            </div>
                                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 18 }}>
                                                                {detailItem('Late', delivery.late == null ? '----' : delivery.late ? 'Yes' : 'No')}
                                                                {detailItem('Late Reason', delivery.late_reason || '----')}
                                                                {detailItem('Delay Reason', delivery.delay_reason || '----')}
                                                                {detailItem('Penalties', delivery.penalties == null ? '----' : `£${Number(delivery.penalties).toFixed(2)}`)}
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <p style={{ opacity: 0.7 }}>No delivery information recorded.</p>
                                                    )}
                                                </section>
                                                <section style={{ borderTop: `1px solid ${isLightTheme ? '#cbd5e1' : '#555'}`, paddingTop: 18 }}>
                                                    <h4 style={{ margin: '0 0 14px' }}>POD Information</h4>
                                                    {completion ? (
                                                        <>
                                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 18 }}>
                                                                {detailItem('Signed By', completion.signed_by || '----')}
                                                                {detailItem('Signed At', formatDateTime(completion.signed_at))}
                                                                {detailItem('Submitted', formatDateTime(completion.submitted_at))}
                                                                {detailItem('Location', hasCoordinates ? (
                                                                    <a
                                                                        href={`https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`}
                                                                        target="_blank"
                                                                        rel="noreferrer"
                                                                        style={{ color: isLightTheme ? '#075985' : '#7dd3fc' }}
                                                                    >
                                                                        {completion.geocoded_address && <span style={{ display: 'block' }}>{completion.geocoded_address}</span>}
                                                                        <span>{latitude.toFixed(5)}, {longitude.toFixed(5)}</span>
                                                                        {completion.location_accuracy != null && (
                                                                            <span style={{ fontWeight: 400 }}> (±{Math.round(Number(completion.location_accuracy))}m)</span>
                                                                        )}
                                                                        <span style={{ display: 'block', fontSize: 12, fontWeight: 400 }}>Open in Google Maps</span>
                                                                    </a>
                                                                ) : completion.geocoded_address || '----')}
                                                            </div>
                                                            {(completion.completion_notes || completion.driver_comments) && (
                                                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 18, marginTop: 18 }}>
                                                                    {completion.completion_notes && detailItem('Completion Notes', completion.completion_notes)}
                                                                    {completion.driver_comments && detailItem('Driver Comments', completion.driver_comments)}
                                                                </div>
                                                            )}
                                                            {(signature || completion.delivery_images?.length > 0 || completion.paperwork_images?.length > 0) && (
                                                                <div style={{
                                                                    display: 'grid',
                                                                    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                                                                    alignItems: 'start',
                                                                    gap: 24,
                                                                    marginTop: 22,
                                                                    paddingTop: 18,
                                                                    borderTop: `1px solid ${isLightTheme ? '#e2e8f0' : '#444'}`,
                                                                }}>
                                                                    {signature && imageList('Signature', [signature], true)}
                                                                    {imageList('Delivery Photos', completion.delivery_images)}
                                                                    {imageList('Paperwork', completion.paperwork_images)}
                                                                </div>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <p style={{ opacity: 0.7 }}>No POD information recorded.</p>
                                                    )}
                                                </section>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            </React.Fragment>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export default NetworkFreightConsignmentReport;